import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageShell } from "@/components/page-shell";
import {
  ArticleBody,
  DotList,
  EndNote,
  MoreLinks,
  Prose,
  ReviewedDate,
  RuledList,
  Section,
  SourcesAndDisclaimer,
  Subhead,
} from "@/components/guide-page";
import { InlineLinks } from "@/components/checklist";
import type { TocItem } from "@/components/toc";
import { Faq, faqJsonLd } from "@/components/faq";
import { SourceList } from "@/components/source-list";
import { ShareCard } from "@/components/share-card";
// Value import used only by the component below, so the route splitter keeps it in
// the component chunk; the loader imports the module dynamically for the same reason.
import { getSpecies, type Species } from "@/lib/species";
import { getDisease } from "@/lib/diseases";
import { breadcrumbJsonLd, articleJsonLd } from "@/lib/jsonld";
import { SPECIES_SAME_AS, taxonEntity } from "@/lib/entities";
import { absUrl, ogImageUrl } from "@/lib/site";
import { pageTitle } from "@/lib/seo";

export const Route = createFileRoute("/ticks/$species")({
  // The content module is imported dynamically so it lands in this route's own chunk
  // instead of the shared entry chunk that every page downloads. routeTree.gen.ts
  // imports each route's loader and head eagerly, so a top-level import here would
  // put this file's prose on the home page's critical path.
  loader: async ({ params }): Promise<{ species: Species }> => {
    const { getSpecies: lookup } = await import("@/lib/species");
    const species = lookup(params.species);
    if (!species) throw notFound();
    return { species };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.species;
    if (!s) return {};
    const path = `/ticks/${s.slug}`;
    const ogImage = ogImageUrl({
      title: s.commonName,
      subtitle: s.scientificName,
      kind: "guide",
    });
    const scripts = [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          articleJsonLd({
            headline: s.h1,
            description: s.metaDescription,
            path,
            datePublished: s.datePublished,
            dateModified: s.lastReviewed,
            section: "Tick species",
            keywords: s.keywords,
            about: [
              taxonEntity({
                name: s.commonName,
                scientificName: s.scientificName,
                sameAs: SPECIES_SAME_AS[s.slug],
              }),
            ],
            image: s.image ? absUrl(s.image.src) : ogImage,
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(s.faq)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Tick species", path: "/ticks" },
            { name: s.commonName, path },
          ]),
        ),
      },
    ];
    if (s.image) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageObject",
          contentUrl: absUrl(s.image.src),
          caption: s.image.alt,
          creditText: s.image.credit,
          creator: { "@type": "Organization", name: "Centers for Disease Control and Prevention" },
          license: s.image.license,
          ...(s.image.detailsUrl ? { acquireLicensePage: s.image.detailsUrl } : {}),
        }),
      });
    }
    return {
      meta: [
        { title: pageTitle(s.metaTitle) },
        { name: "description", content: s.metaDescription },
        { name: "keywords", content: s.keywords.join(", ") },
        { property: "og:title", content: s.h1 },
        { property: "og:description", content: s.metaDescription },
        { property: "og:url", content: absUrl(path) },
        { property: "og:type", content: "article" },
        { property: "og:image", content: s.image ? absUrl(s.image.src) : ogImage },
        { name: "twitter:title", content: s.h1 },
        { name: "twitter:description", content: s.metaDescription },
        { name: "twitter:image", content: s.image ? absUrl(s.image.src) : ogImage },
      ],
      links: [{ rel: "canonical", href: absUrl(path) }],
      scripts,
    };
  },
  notFoundComponent: () => (
    <PageShell title="Tick species not found">
      <p className="text-sm text-muted-foreground">
        We don't have a page for that species yet.{" "}
        <Link to="/ticks" className="link font-semibold">
          Browse tick species
        </Link>
        .
      </p>
    </PageShell>
  ),
  component: SpeciesPage,
});

function SpeciesPage() {
  const { species: s } = Route.useLoaderData() as { species: Species };
  const diseases = s.diseasesCarried.map(getDisease).filter((x) => x !== undefined);
  const related = s.relatedSpeciesSlugs.map(getSpecies).filter((x) => x !== undefined);
  const toc: TocItem[] = [
    { id: "identify", label: "How to identify it" },
    { id: "range", label: "Where it lives and when" },
    { id: "diseases", label: "Diseases it can carry" },
    { id: "look-alikes", label: "Look-alikes" },
    { id: "common-questions", label: "Common questions" },
    { id: "sources", label: "Sources" },
  ];

  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Tick species", path: "/ticks" },
        { name: s.commonName, path: `/ticks/${s.slug}` },
      ]}
      title={s.h1}
      intro={s.intro}
    >
      <ReviewedDate iso={s.lastReviewed} />

      <ArticleBody toc={toc}>
        {s.image && (
          <figure className="max-w-md">
            <img
              src={s.image.src}
              alt={s.image.alt}
              width={s.image.width ?? 575}
              height={s.image.height ?? 546}
              loading="lazy"
              decoding="async"
              className="w-full rounded-sm border border-border"
            />
            <figcaption className="mt-2 text-xs leading-relaxed text-muted-foreground">
              <span className="text-foreground">{s.commonName}</span> (<i>{s.scientificName}</i>).
              Photo: {s.image.credit} (public domain
              {s.image.detailsUrl && (
                <>
                  ,{" "}
                  <a
                    href={s.image.detailsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link print-no-url"
                  >
                    PHIL page
                    <span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </>
              )}
              ).
            </figcaption>
          </figure>
        )}

        <Section id="identify" title="How to identify it">
          <RuledList items={s.identMarks} />
          <div>
            <Subhead>Size by life stage</Subhead>
            <Prose>{s.sizeByStage}</Prose>
          </div>
        </Section>

        <Section id="range" title="Where it lives and when">
          <Prose>{s.range}</Prose>
          <div>
            <Subhead>Season</Subhead>
            <Prose>{s.seasonality}</Prose>
          </div>
          <div>
            <Subhead>Where it waits</Subhead>
            <DotList items={s.habitats} />
          </div>
        </Section>

        <Section id="diseases" title="Diseases it can carry">
          {diseases.length > 0 ? (
            <>
              <Prose>
                Tick presence is not the same as personal disease risk. Not every bite transmits a
                pathogen.
              </Prose>
              <InlineLinks className="text-[1.0625rem]">
                {diseases.map((d) => (
                  <li key={d.slug}>
                    {/* Conditions with a flagship page (alpha-gal) link straight there, no redirect hop. */}
                    {d.canonicalPath ? (
                      <Link to={d.canonicalPath} className="link">
                        {d.name}
                      </Link>
                    ) : (
                      <Link to="/diseases/$disease" params={{ disease: d.slug }} className="link">
                        {d.name}
                      </Link>
                    )}
                  </li>
                ))}
              </InlineLinks>
            </>
          ) : s.diseasesNote ? (
            <Prose>{s.diseasesNote}</Prose>
          ) : (
            <Prose>
              No major human disease is firmly established for this species in the U.S. Prevent
              bites regardless.
            </Prose>
          )}
        </Section>

        <Section id="look-alikes" title="Look-alikes">
          <Prose>{s.lookAlikes}</Prose>
          <Link to="/tick-identification" className="link inline-block text-sm font-semibold">
            Compare all tick species
          </Link>
        </Section>

        <Section id="common-questions" title="Common questions">
          <Faq items={s.faq} />
        </Section>

        <div id="sources">
          <SourceList ids={s.sourceIds} />
        </div>

        {related.length > 0 && (
          <div>
            <MoreLinks id="related-species" title="Related tick species">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link to="/ticks/$species" params={{ species: r.slug }} className="link">
                    {r.commonName}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/ticks" className="link font-semibold">
                  All tick species
                </Link>
              </li>
            </MoreLinks>
          </div>
        )}

        <ShareCard
          title={s.h1}
          subtitle={s.metaDescription}
          kind="guide"
          summaryText={`${s.h1}\n\n${s.intro}\n\nField guide: https://lonestarmap.com/ticks/${s.slug}`}
          sharePath={`/ticks/${s.slug}`}
          shareText={`${s.commonName} (${s.scientificName}), from the LoneStarMap field guide:`}
        />

        <EndNote>
          Educational information only, not medical advice. If you were bitten, use the{" "}
          <Link to="/tick-bite-tool" className="link">
            tick bite tool
          </Link>{" "}
          and contact a licensed clinician if symptoms develop. <SourcesAndDisclaimer />
        </EndNote>
      </ArticleBody>
    </PageShell>
  );
}
